import {
  CT,
  EVAP,
  FREEZE,
  FS,
  INIT_CORALS,
  MAX_CORALS,
  MAX_FISH,
  MAX_SPAWN,
} from "./constants";

let nextCoralId = INIT_CORALS;

const randBetween = (a, b) => a + Math.random() * (b - a);
const randInt = (a) => Math.floor(Math.random() * a);
const randomFishSpecies = () => FS[randInt(FS.length)];
const randomCoralType = () => CT[randInt(CT.length)];

export function resetCoralIdCounter() {
  nextCoralId = INIT_CORALS;
}

export function getSSP245Rate(year) {
  if (year < 2040) return 0.033;
  if (year < 2055) return 0.028;
  if (year < 2070) return 0.02;
  if (year < 2090) return 0.012;
  return 0.006;
}

export function getSaveRate(corals, oceanTemp) {
  let aliveCount = 0;
  let totalAlgae = 0;

  for (const coral of corals) {
    if (coral.alive) {
      aliveCount += 1;
      totalAlgae += coral.alg;
    }
  }

  const aliveRatio = aliveCount / Math.max(1, corals.length);
  const avgAlgae = aliveCount > 0 ? totalAlgae / aliveCount : 0;
  const diff = oceanTemp - 25;

  let rate = -diff * 0.08;
  if (aliveRatio < 0.3) rate -= 0.5;
  else if (aliveRatio < 0.5) rate -= 0.2;
  else if (aliveRatio < 0.8) rate -= 0.05;

  if (avgAlgae < 20) rate -= 0.4;
  else if (avgAlgae < 40) rate -= 0.15;
  else if (avgAlgae < 70) rate -= 0.03;

  if (oceanTemp > 35) rate -= Math.min(1, (oceanTemp - 35) * 0.15);
  if (oceanTemp < 20) rate += Math.min(0.5, (20 - oceanTemp) * 0.1);
  if (oceanTemp < 24 && aliveRatio > 0.9 && avgAlgae > 80) rate = Math.max(rate, 0.005);

  return Math.max(-2, Math.min(0.5, rate));
}

export function mkFish(count, fromEdge) {
  return Array.from({ length: count }, (_, i) => {
    const sp = randomFishSpecies();
    const left = Math.random() > 0.5;

    return {
      id: Date.now() + i,
      sp,
      alive: true,
      age: randInt(fromEdge ? 8 : 3),
      fleeing: false,
      fleeDir: 0,
      hiding: false,
      hideTimer: 0,
      x: fromEdge ? (left ? -10 : 970) : 60 + Math.random() * 840,
      y: 80 + Math.random() * 350,
      vx: fromEdge ? (left ? 1.5 : -1.5) : (Math.random() - 0.5) * 2,
      vy: (Math.random() - 0.5) * 0.5,
    };
  });
}

function mkCoral(gx, gy, age, alg) {
  const type = randomCoralType();
  return {
    id: nextCoralId++,
    gx,
    gy,
    ox: (Math.random() - 0.5) * 18,
    oy: (Math.random() - 0.5) * 12,
    type,
    res: 0.3 + Math.random() * 0.7,
    size: alg > 50 ? 6 + Math.random() * 5 : 3 + Math.random() * 2,
    alg,
    alive: true,
    bt: 0.8 + Math.random() * 0.4,
    age,
    ls: Math.round(randBetween(43, 67)),
    cod: null,
    ta: 0,
  };
}

export function mkCorals() {
  const corals = [];
  for (let i = 0; i < INIT_CORALS; i++) {
    corals.push(mkCoral(i % 24, Math.floor(i / 24), randInt(20), 100));
  }
  resetCoralIdCounter();
  return corals;
}

export function stepSim(corals, fishPopulation, oceanTemp, baselineTemp) {
  const tempAboveBaseline = oceanTemp - baselineTemp;
  const frozen = oceanTemp <= FREEZE;
  const evaporated = oceanTemp >= EVAP;
  const idealRange = oceanTemp >= 24 && oceanTemp <= 27;
  const turfRange = oceanTemp >= 25 && oceanTemp <= 32;

  let simCorals = corals.map((coral) => {
    if (frozen && coral.alive) return { ...coral, age: coral.age + 1, alive: false, alg: 0, cod: "frozen", ta: 0 };
    if (evaporated && coral.alive) return { ...coral, age: coral.age + 1, alive: false, alg: 0, cod: "evaporated", ta: 0 };

    if (!coral.alive) {
      let turf = coral.ta;
      if (turfRange && turf < 100) turf = Math.min(100, turf + 2 + Math.random() * 3);
      if (idealRange && turf < 60 && Math.random() < 0.123) {
        return {
          ...coral,
          alive: true,
          alg: 30 + Math.random() * 20,
          cod: null,
          ta: 0,
          age: 0,
          ls: Math.round(randBetween(43, 67)),
          size: 3 + Math.random() * 2,
        };
      }
      return { ...coral, ta: turf };
    }

    const nextAge = coral.age + 1;
    if (nextAge >= coral.ls) return { ...coral, age: nextAge, alive: false, alg: 0, cod: "age", ta: 0 };

    const stress = tempAboveBaseline - coral.bt * coral.res;
    const coldStress = oceanTemp < 20 ? (20 - oceanTemp) * 0.8 : 0;

    let algae = coral.alg;
    if (stress > 0) algae = Math.max(0, algae - stress * (2.2 / coral.res));
    else if (coldStress > 0) algae = Math.max(0, algae - coldStress / coral.res);
    else algae = Math.min(100, algae + Math.min(2.5, 0.3 + Math.abs(stress) * 0.5) * coral.res);

    const alive = algae > 5;
    return {
      ...coral,
      alg: algae,
      alive,
      age: nextAge,
      size: alive ? Math.min(13, coral.size + (algae > 60 ? 0.015 * coral.res : 0)) : coral.size,
      cod: alive ? null : coldStress > 0 ? "cold" : "bleaching",
      ta: alive ? coral.ta : 0,
    };
  });

  if (!frozen && !evaporated && simCorals.length < MAX_CORALS) {
    const spawn = [];
    for (const coral of simCorals) {
      if (!coral.alive || coral.age < 21 || coral.age > 40 || coral.alg <= 70) continue;
      if (spawn.length >= MAX_SPAWN || simCorals.length + spawn.length >= MAX_CORALS) break;

      const healthFactor = (coral.alg - 70) / 30;
      const tempFactor = tempAboveBaseline < 0.3 ? 1 : tempAboveBaseline < 1 ? 0.7 : tempAboveBaseline < 2 ? 0.3 : 0.05;
      if (Math.random() < 0.37 * coral.type.rm * healthFactor * tempFactor) {
        spawn.push(mkCoral(coral.gx + (Math.random() - 0.5) * 5, coral.gy + (Math.random() - 0.5) * 5, 0, 85 + Math.random() * 15));
      }
    }
    simCorals = simCorals.concat(spawn);
  }

  let aliveCoralCount = 0;
  let totalAliveAlgae = 0;
  for (const coral of simCorals) {
    if (coral.alive) {
      aliveCoralCount += 1;
      totalAliveAlgae += coral.alg;
    }
  }

  const coralRatio = aliveCoralCount / Math.max(1, simCorals.length);
  const avgAliveAlgae = aliveCoralCount > 0 ? totalAliveAlgae / aliveCoralCount : 0;
  const targetFraction = avgAliveAlgae <= 5 ? 0 : avgAliveAlgae <= 20 ? 0.05 : avgAliveAlgae <= 50 ? 0.15 : avgAliveAlgae <= 80 ? 0.35 : 1;

  let presentFish = 0;
  for (const fish of fishPopulation) {
    if (fish.alive && !fish.fleeing) presentFish += 1;
  }

  const targetFish = Math.max(4, Math.round(targetFraction * MAX_FISH * coralRatio));
  const tooManyFish = presentFish > targetFish;

  let simFish = fishPopulation.map((fish) => {
    if (!fish.alive) return fish;
    if (fish.fleeing) return fish.x < -50 || fish.x > 1010 ? { ...fish, alive: false } : fish;
    if (frozen || evaporated) return { ...fish, fleeing: true, fleeDir: fish.x > 480 ? 1 : -1 };

    const nextAge = fish.age + 1;
    const maxAge = 20 + Math.floor((1 - fish.sp.dep) * 15);
    if (nextAge >= maxAge || (tooManyFish && Math.random() < 0.04)) {
      return { ...fish, fleeing: true, fleeDir: fish.x > 480 ? 1 : -1, age: nextAge };
    }

    return { ...fish, age: nextAge };
  });

  simFish = simFish.filter((fish) => fish.alive || (fish.fleeing && fish.x > -50 && fish.x < 1010));

  if (!frozen && !evaporated) {
    const newbornFish = [];
    let aliveFishCount = 0;
    let nonFleeingFishCount = 0;

    for (const fish of simFish) {
      if (fish.alive) aliveFishCount += 1;
      if (fish.alive && !fish.fleeing) nonFleeingFishCount += 1;
    }

    if (avgAliveAlgae > 40) {
      for (const fish of simFish) {
        if (!(fish.alive && !fish.fleeing && fish.age >= 2)) continue;
        if (aliveFishCount + newbornFish.length >= MAX_FISH) break;
        if (Math.random() < fish.sp.br * Math.max(0.2, targetFraction)) {
          newbornFish.push(...mkFish(1, true));
        }
      }
    }

    if (nonFleeingFishCount + newbornFish.length < targetFish && avgAliveAlgae > 30) {
      const deficit = targetFish - nonFleeingFishCount - newbornFish.length;
      const arrivals = Math.min(deficit, Math.max(3, Math.ceil(deficit * 0.3)));
      for (let i = 0; i < arrivals && aliveFishCount + newbornFish.length < MAX_FISH; i++) {
        newbornFish.push(...mkFish(1, true));
      }
    }

    simFish = simFish.concat(newbornFish);
  }

  return { corals: simCorals, fish: simFish };
}

export function applyBoids(fish, width, height, waterY) {
  const alive = fish.filter((f) => f.alive && !f.fleeing);
  if (!alive.length) return;

  const MAX_PER_SCHOOL = 32;
  const VIEW_RADIUS = 80;
  const MIN_DISTANCE = 20;
  const MATCH_FORCE = 0.05;
  const CENTER_FORCE = 0.003;
  const AVOID_FORCE = 0.08;
  const EDGE_MARGIN = 40;
  const TURN_FORCE = 0.3;
  const SPEED_LIMIT = 2.5;
  const MIN_SPEED = 0.4;

  const fishMinY = waterY + 25;
  const fishMaxY = height - 45;
  const schoolCount = Math.max(4, Math.ceil(alive.length / MAX_PER_SCHOOL));

  for (const f of alive) {
    if (f.schoolId === undefined || f.schoolId >= schoolCount) f.schoolId = randInt(schoolCount);
  }

  const schoolSizes = new Array(schoolCount).fill(0);
  for (const f of alive) schoolSizes[f.schoolId] += 1;

  for (const f of alive) {
    if (schoolSizes[f.schoolId] <= MAX_PER_SCHOOL) continue;
    schoolSizes[f.schoolId] -= 1;

    let smallest = 0;
    for (let i = 1; i < schoolCount; i++) {
      if (schoolSizes[i] < schoolSizes[smallest]) smallest = i;
    }

    f.schoolId = smallest;
    schoolSizes[smallest] += 1;
  }

  const cellSize = VIEW_RADIUS;
  const grid = new Map();

  const gridKey = (x, y) => `${x},${y}`;

  for (const f of alive) {
    const gx = Math.floor(f.x / cellSize);
    const gy = Math.floor(f.y / cellSize);
    const key = gridKey(gx, gy);
    if (!grid.has(key)) grid.set(key, []);
    grid.get(key).push(f);
  }

  for (const f of alive) {
    let centerX = 0;
    let centerY = 0;
    let centerCount = 0;
    let alignX = 0;
    let alignY = 0;
    let avoidX = 0;
    let avoidY = 0;

    const gx = Math.floor(f.x / cellSize);
    const gy = Math.floor(f.y / cellSize);

    for (let ix = gx - 1; ix <= gx + 1; ix++) {
      for (let iy = gy - 1; iy <= gy + 1; iy++) {
        const neighbors = grid.get(gridKey(ix, iy));
        if (!neighbors) continue;

        for (const o of neighbors) {
          if (o === f) continue;
          const dx = o.x - f.x;
          const dy = o.y - f.y;
          const d = Math.sqrt(dx * dx + dy * dy);

          if (d < VIEW_RADIUS && o.schoolId === f.schoolId && centerCount < MAX_PER_SCHOOL) {
            centerX += o.x;
            centerY += o.y;
            centerCount += 1;
            alignX += o.vx;
            alignY += o.vy;
          }

          if (d < MIN_DISTANCE) {
            avoidX -= dx / Math.max(1, d);
            avoidY -= dy / Math.max(1, d);
          }
        }
      }
    }

    if (centerCount > 0) {
      f.vx += (centerX / centerCount - f.x) * CENTER_FORCE;
      f.vy += (centerY / centerCount - f.y) * CENTER_FORCE;
      f.vx += (alignX / centerCount - f.vx) * MATCH_FORCE;
      f.vy += (alignY / centerCount - f.vy) * MATCH_FORCE;
    }

    f.vx += avoidX * AVOID_FORCE;
    f.vy += avoidY * AVOID_FORCE;

    if (f.x < EDGE_MARGIN) f.vx += TURN_FORCE;
    if (f.x > width - EDGE_MARGIN) f.vx -= TURN_FORCE;
    if (f.y < fishMinY + 10) f.vy += TURN_FORCE;
    if (f.y > fishMaxY - 10) f.vy -= TURN_FORCE;

    const speed = Math.sqrt(f.vx * f.vx + f.vy * f.vy);
    if (speed > SPEED_LIMIT) {
      f.vx = (f.vx / speed) * SPEED_LIMIT;
      f.vy = (f.vy / speed) * SPEED_LIMIT;
    }
    if (speed < MIN_SPEED && speed > 0) {
      f.vx = (f.vx / speed) * MIN_SPEED;
      f.vy = (f.vy / speed) * MIN_SPEED;
    }

    f.x += f.vx;
    f.y += f.vy;
    f.y = Math.max(fishMinY, Math.min(fishMaxY, f.y));
  }

  for (const f of fish) {
    if (f.alive && f.fleeing) f.x += f.fleeDir * 3;
  }

  for (const f of alive) {
    if (f.hiding) {
      f.hideTimer -= 1;
      if (f.hideTimer <= 0) f.hiding = false;
    } else if (Math.random() < 0.0005 && f.y > fishMaxY - 100) {
      f.hiding = true;
      f.hideTimer = 40 + randInt(80);
    }
  }
}

export function mkWavePoints(count) {
  return Array.from({ length: count }, (_, i) => ({
    x: (i / count) * 960 + (Math.random() - 0.5) * 20,
    baseY: 0,
    phase: Math.random() * Math.PI * 2,
    amp: 3 + Math.random() * 4,
  }));
}

export function toIso(gx, gy, ox, oy, width, height, waterY) {
  const rawY = 30 + (gx + gy) * 11 + oy;
  return [width / 2 - 30 + (gx - gy) * 18 + ox, waterY + (rawY * (height - waterY)) / height];
}

export function computeEcosystemStats(corals, fishList, baselineTemp, oceanTemp) {
  const stats = {
    totalCorals: corals.length,
    aliveCorals: 0,
    deadCorals: 0,
    totalAliveAlgae: 0,
    breedable: 0,
    healthy: 0,
    stressed: 0,
    bleaching: 0,
    critical: 0,
    turfCoverage: 0,
    bleachingDeaths: 0,
    ageDeaths: 0,
    frozenDeaths: 0,
    evaporatedDeaths: 0,
    coldDeaths: 0,
    speciesAlive: Object.fromEntries(CT.map((t) => [t.n, 0])),
    aliveFish: 0,
    fleeingFish: 0,
    totalFish: fishList.length,
    fishSpeciesAlive: Object.fromEntries(FS.map((f) => [f.n, 0])),
    tempChange: oceanTemp - baselineTemp,
  };

  for (const coral of corals) {
    if (coral.alive) {
      stats.aliveCorals += 1;
      stats.totalAliveAlgae += coral.alg;
      stats.speciesAlive[coral.type.n] += 1;

      if (coral.age >= 21 && coral.age <= 40 && coral.alg > 70) stats.breedable += 1;
      if (coral.alg > 80) stats.healthy += 1;
      else if (coral.alg > 50) stats.stressed += 1;
      else if (coral.alg > 20) stats.bleaching += 1;
      else stats.critical += 1;
    } else {
      stats.deadCorals += 1;
      if (coral.ta > 50) stats.turfCoverage += 1;
      if (coral.cod === "bleaching") stats.bleachingDeaths += 1;
      if (coral.cod === "age") stats.ageDeaths += 1;
      if (coral.cod === "frozen") stats.frozenDeaths += 1;
      if (coral.cod === "evaporated") stats.evaporatedDeaths += 1;
      if (coral.cod === "cold") stats.coldDeaths += 1;
    }
  }

  for (const fish of fishList) {
    if (!fish.alive) continue;
    if (fish.fleeing) stats.fleeingFish += 1;
    else {
      stats.aliveFish += 1;
      stats.fishSpeciesAlive[fish.sp.n] += 1;
    }
  }

  stats.avgAlgae = stats.aliveCorals > 0 ? stats.totalAliveAlgae / stats.aliveCorals : 0;
  stats.fishPct = Math.round((stats.aliveFish / Math.max(1, MAX_FISH)) * 100);

  return stats;
}
